import { getShellInfo, isInShell } from '../nativeBridge/shellPresence';

import { PlatformEnum, type PlatformContextConfig } from './platformContextTypes';

class PlatformContextService {
  private config: PlatformContextConfig = {
    platform: PlatformEnum.WEB,
    paymentEmitterPlatform: undefined,
  };

  private getCorrectPlatform(input: string | null): PlatformEnum {
    switch (input) {
      case PlatformEnum.APP_IOS:
      case PlatformEnum.APP_ANDROID:
      case PlatformEnum.WEB:
        return input;
      default:
        return PlatformEnum.WEB;
    }
  }

  /**
   * The shell's own platform, for a document it loaded without `?platform=`. Only its first URL carries the
   * param; a same-origin `_blank` link it opens with `window.location.href` starts a new document without it,
   * which would otherwise report `WEB` for analytics and payments from then on. The shell seeds `app.info`
   * before any page script runs, so it is readable here at boot.
   */
  private getShellPlatformParam(): PlatformEnum | null {
    switch (getShellInfo()?.platform) {
      case 'ios':
        return PlatformEnum.APP_IOS;
      case 'android':
        return PlatformEnum.APP_ANDROID;
      default:
        return null;
    }
  }

  public initFromURL(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const platformParam = urlParams.get('platform') ?? this.getShellPlatformParam();
    const paymentEmitterPlatformParam = urlParams.get('paymentEmitterPlatform');

    this.setConfig(
      this.getCorrectPlatform(platformParam),
      paymentEmitterPlatformParam ? this.getCorrectPlatform(paymentEmitterPlatformParam) : undefined,
    );
  }

  private setConfig(platform: PlatformEnum, paymentEmitterPlatform?: PlatformEnum): void {
    this.config.platform = platform;
    this.config.paymentEmitterPlatform = paymentEmitterPlatform;
  }

  public getConfig(): PlatformContextConfig {
    return this.config;
  }

  /**
   * Where the payments/redeem iframe sends its messages. Unset, the iframe prefers a React Native WebView
   * handler whenever one is visible — and react-native-webview exposes it in every frame, not only the top
   * document. Inside the mobile shell this page is the iframe's host and the shell drops what it receives, so
   * the iframe is told `WEB` and posts to `window.parent`, where `ExternalPayments` listens.
   */
  public getPaymentEmitterPlatform(): PlatformEnum | undefined {
    return this.config.paymentEmitterPlatform ?? (isInShell() ? PlatformEnum.WEB : undefined);
  }

  /**
   * The `platform` for every payment and redeem: the iframe URLs, and this page's own purchase and redeem calls
   * (`PayRedeemStore`, `useTokenEXPayment`). Inside the mobile shell this page, not a native app, hosts the
   * payment, so it is `WEB`: with `APP_*`, payments-frontend reports Apple/Google Pay as available without
   * asking the pay.com SDK, and player-backend sends the Skrill/PayByBank purchase return and the redeem-setup
   * return through native-app paths (`/securepay/back-to-lobby`) whose message the shell drops. Analytics keep
   * the shell's `APP_*` (`getConfig().platform`).
   */
  public getPaymentPlatform(): PlatformEnum {
    return isInShell() ? PlatformEnum.WEB : this.config.platform;
  }
}

export const platformContextService = new PlatformContextService();
